import { isNil, cloneDeep } from 'lodash-es';
import { formatDuration } from '~/utils/time_utils';
import { ACTIVITY_BENCHMARKS } from '~/app_constants';

/**
 * Transforms the benchmarks API response to a
 * format that represents/maps to the UI.
 *
 * To see the final representation of the data,
 * see "ACTIVITY_BENCHMARKS" constant
 * in `app_constants.js` file.
 */
export function mapBenchmarks(benchmarks) {
  // NOTE: Use `let` to MARK it as mutable.
  const benchmarksWithValues = cloneDeep(ACTIVITY_BENCHMARKS);

  for (const key in benchmarks) {
    const parts = key.split('_');
    const panel = parts.shift();
    const metric = parts.join('_');

    benchmarksWithValues[panel].items[metric].value = benchmarks[key];
  }

  append(benchmarksWithValues.time.items.prev_day_comparison);
  appendFromAverage(benchmarksWithValues.activity.items.average);
  append(benchmarksWithValues.activity.items.prev_day_comparison);
  append(benchmarksWithValues.activity.items.organization_average);
  appendHighlight(benchmarksWithValues.tasks.items.completed_count);
  append(benchmarksWithValues.tasks.items.prev_day_comparison);

  /**
   * Process each panel's items' item, mapping its value
   * to the correct representation needed by the UI.
   */
  for (const panelKey in benchmarksWithValues) {
    const { items } = benchmarksWithValues[panelKey];

    for (const itemKey in items) {
      const item = items[itemKey];
      const { value, type } = item;
      const parsedValue = value ? Math.abs(value) : null;

      // Initialize it with the default value
      item.value = item.default;

      if (parsedValue) {
        switch (type) {
          case 'percentage':
            item.value = `${Math.round(parsedValue * 100)}%`;
            break;
          case 'time':
            item.value = formatDuration(parsedValue, { showSeconds: 'never' });
            break;
          default:
            item.value = parsedValue;
        }
      }
    }
  }

  return benchmarksWithValues;
}

function append(field) {
  if (field.value) {
    const isTrue = field.value > 0;

    field.arrow = isTrue ? 'asc' : 'desc';
    field.trends = isTrue ? 'better' : 'worse';
  }
}

function appendHighlight(field) {
  if (field.value) {
    field.trends = 'highlight';
  }
}

function appendFromAverage(field) {
  if (field.value) {
    let activeClass = 'better';

    if (field.value <= 0.2) {
      activeClass = 'worse';
    } else if (field.value > 0.2 && field.value <= 0.5) {
      activeClass = 'good';
    }

    field.trends = activeClass;
  }
}

/**
 * Helper function to help calculate the "correct"
 * classes based on an average.
 */
export function getAverageRange(average) {
  // If the value is not present or for some reason is less than 0,
  // assume "idle" instead of trying to compare it against ranges.
  if (isNil(average) || average < 0) {
    return 'idle';
  }

  if (average >= 51) {
    return 'high';
  }
  if (average >= 21 && average <= 50) {
    return 'medium';
  }
  if (average >= 0 && average <= 20) {
    return 'low';
  }
}
